import React, { useState, useEffect } from 'react';
import { X, Sparkles, Copy, Check, Loader, Wand2 } from 'lucide-react';
import { generateContentWithAI, quickPrompts, getPlatformOptimizedPrompt } from '../firebase/aiService';

/**
 * AI ile içerik oluşturma modal bileşeni
 * Kullanıcının bağlı platformları için otomatik içerik oluşturur
 */
const AIContentModal = ({ isOpen, onClose, mediaFiles = [], onUseContent, connectedPlatforms = [] }) => {
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(null);

  // Placeholder metnini dinamik oluştur
  const getPlaceholder = () => {
    if (mediaFiles.length > 0) {
      const imageCount = mediaFiles.filter(m => m.type === 'image').length;
      const videoCount = mediaFiles.filter(m => m.type === 'video').length;

      if (imageCount > 0 && videoCount > 0) {
        return `Bu ${imageCount} görsel ve ${videoCount} video hakkında ne paylaşmak istersiniz?\n\nÖrn: Yeni ürünlerimizi tanıtın, özellikleri vurgulayın...`;
      } else if (imageCount > 0) {
        return `Bu ${imageCount} görsel hakkında ne paylaşmak istersiniz?\n\nÖrn: Yeni koleksiyonumuzu tanıtın, renkleri ve özellikleri vurgulayın...`;
      } else {
        return `Bu ${videoCount} video hakkında ne paylaşmak istersiniz?\n\nÖrn: Video içeriğini özetleyin ve izleyicileri heyecanlandırın...`;
      }
    }
    return 'Ne hakkında içerik oluşturmak istersiniz?\n\nÖrn: Yaz indirimleri başladı! %50\'ye varan indirimlerden bahset...';
  };

  // Modal açıldığında seçili stili temizle
  useEffect(() => {
    if (isOpen) {
      setSelectedStyle(null);
      setPrompt('');
    }
  }, [isOpen]);

  // AI ile içerik oluştur
  const handleGenerate = async () => {
    // Stil seçilmişse ama prompt yoksa, stilin promptunu kullan
    let finalPrompt = prompt.trim();

    if (!finalPrompt && selectedStyle) {
      const stylePrompt = quickPrompts.find(qp => qp.id === selectedStyle);
      if (stylePrompt) {
        finalPrompt = stylePrompt.prompt;
      }
    }

    if (!finalPrompt) {
      setError('Lütfen bir açıklama yazın veya bir stil seçin!');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedContent('');

    try {
      // Stil seçilmişse prompt'a ekle
      let enhancedPrompt = finalPrompt;
      if (selectedStyle) {
        const stylePrompt = quickPrompts.find(qp => qp.id === selectedStyle);
        if (stylePrompt) {
          enhancedPrompt = `${stylePrompt.prompt}\n\nEk detaylar: ${finalPrompt}`;
        }
      }

      // Kullanıcının bağlı olduğu ilk platformu kullan (veya instagram default)
      const primaryPlatform = connectedPlatforms.length > 0 ? connectedPlatforms[0].id : 'instagram';

      // Platform optimizasyonlu prompt oluştur
      const optimizedPrompt = getPlatformOptimizedPrompt(
        primaryPlatform,
        enhancedPrompt,
        mediaFiles.length > 0
      );

      console.log('🤖 AI generate başlatılıyor...');
      console.log('Platform:', primaryPlatform);
      console.log('Stil:', selectedStyle);
      console.log('Medya sayısı:', mediaFiles.length);

      const result = await generateContentWithAI(
        optimizedPrompt,
        mediaFiles,
        {
          model: mediaFiles.length > 0 ? 'gpt-4o' : 'gpt-4o-mini',
          max_tokens: 1000,
          temperature: 0.7
        }
      );

      if (result.success) {
        setGeneratedContent(result.text);
        console.log('✅ İçerik oluşturuldu:', result.text.substring(0, 100));

        // Token kullanım bilgisi
        if (result.usage) {
          console.log('📊 Token kullanımı:', result.usage);
        }
      } else {
        setError(result.error || 'İçerik oluşturulamadı');
        console.error('❌ AI hatası:', result.error);
      }
    } catch (err) {
      console.error('❌ Generate hatası:', err);
      setError('Beklenmeyen bir hata oluştu: ' + err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Hızlı stil seç (arka planda kullanılacak)
  const handleStyleSelect = (styleId) => {
    setSelectedStyle(styleId === selectedStyle ? null : styleId);
    setError('');
  };

  // İçeriği kopyala
  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // İçeriği kullan - ana metin alanına aktar
  const handleUseContent = () => {
    if (generatedContent && onUseContent) {
      onUseContent(generatedContent);
      onClose();
    }
  };

  // Modal kapatma
  const handleClose = () => {
    setPrompt('');
    setGeneratedContent('');
    setError('');
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b-2 border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">AI ile İçerik Oluştur</h2>
              <p className="text-sm text-gray-600">ChatGPT ile profesyonel içerikler oluşturun</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Bağlı Platformlar Bilgisi */}
          {connectedPlatforms.length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-purple-900 mb-2">
                🎯 Bağlı Platformlarınız
              </h3>
              <div className="flex flex-wrap gap-2">
                {connectedPlatforms.map(platform => (
                  <div
                    key={platform.id}
                    className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-purple-200"
                  >
                    <span className="text-lg">
                      {platform.id === 'instagram' && '📷'}
                      {platform.id === 'twitter' && '🐦'}
                      {platform.id === 'facebook' && '👍'}
                      {platform.id === 'linkedin' && '💼'}
                      {platform.id === 'tiktok' && '🎵'}
                      {platform.id === 'youtube' && '▶️'}
                      {platform.id === 'pinterest' && '📌'}
                    </span>
                    <span className="text-sm font-semibold text-gray-700">
                      {platform.name}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-purple-700 mt-2">
                💡 İçerik bu platformlara uygun şekilde optimize edilecek
              </p>
            </div>
          )}

          {/* Görseller Önizleme */}
          {mediaFiles.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
              <h3 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                📷 AI Analiz Edecek ({mediaFiles.filter(m => m.type === 'image').length} görsel)
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {mediaFiles
                  .filter(m => m.type === 'image')
                  .slice(0, 4)
                  .map((media, index) => (
                    <div key={index} className="relative">
                      <img
                        src={media.url}
                        alt={`Medya ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                    </div>
                  ))}
              </div>
              {mediaFiles.filter(m => m.type === 'image').length > 4 && (
                <p className="text-xs text-blue-700 mt-2">
                  +{mediaFiles.filter(m => m.type === 'image').length - 4} görsel daha
                </p>
              )}
              {mediaFiles.some(m => m.type === 'video') && (
                <p className="text-xs text-orange-700 mt-2">
                  ⚠️ Video analizi henüz desteklenmiyor
                </p>
              )}
            </div>
          )}

          {/* İçerik Stili Seçimi */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              İçerik Stili (Opsiyonel)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {quickPrompts.map(qp => (
                <button
                  key={qp.id}
                  onClick={() => handleStyleSelect(qp.id)}
                  className={`p-3 border-2 rounded-lg transition text-center ${
                    selectedStyle === qp.id
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-gray-200 hover:border-purple-300 bg-white'
                  }`}
                >
                  <div className="font-semibold text-base text-gray-800">{qp.title}</div>
                  {selectedStyle === qp.id && (
                    <Check className="w-4 h-4 text-purple-600 mx-auto mt-1" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Prompt Girişi */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              İçerik Detayları {selectedStyle && '(Stil: ' + quickPrompts.find(qp => qp.id === selectedStyle)?.title + ')'}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                setError('');
              }}
              placeholder={getPlaceholder()}
              disabled={isGenerating}
              className="w-full h-32 p-4 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none resize-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-500">
                💡 {selectedStyle ? 'Seçtiğiniz stil arka planda uygulanacak' : 'İpucu: Stil seçebilir veya direkt yazabilirsiniz'}
              </p>
              {prompt.length > 0 && (
                <p className="text-xs text-gray-400">{prompt.length} karakter</p>
              )}
            </div>
          </div>

          {/* Oluştur Butonu */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isGenerating ? (
              <>
                <Loader className="w-6 h-6 animate-spin" />
                AI İçerik Oluşturuyor...
              </>
            ) : (
              <>
                <Wand2 className="w-6 h-6" />
                AI ile İçerik Oluştur
              </>
            )}
          </button>

          {/* Hata Mesajı */}
          {error && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
            </div>
          )}

          {/* Oluşturulan İçerik */}
          {generatedContent && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Tarafından Oluşturulan İçerik
                </h3>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-100 transition text-sm font-semibold text-purple-700"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Kopyalandı!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Kopyala
                    </>
                  )}
                </button>
              </div>

              {/* Düzenlenebilir Metin Alanı */}
              <textarea
                value={generatedContent}
                onChange={(e) => setGeneratedContent(e.target.value)}
                className="w-full h-64 p-4 border-2 border-purple-300 rounded-xl focus:border-purple-500 focus:outline-none resize-none bg-white"
              />

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {generatedContent.length} karakter
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setGeneratedContent('');
                      setPrompt('');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-semibold"
                  >
                    Temizle
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition font-semibold disabled:opacity-50"
                  >
                    <Wand2 className="w-4 h-4 inline mr-2" />
                    Yeniden Oluştur
                  </button>
                  <button
                    onClick={handleUseContent}
                    className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:shadow-lg transition font-bold"
                  >
                    <Check className="w-4 h-4 inline mr-2" />
                    Kullan
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bilgilendirme */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-900">
              <strong>💡 Nasıl Çalışır?</strong>
            </p>
            <ul className="text-sm text-purple-800 mt-2 space-y-1 list-disc list-inside">
              <li>Platformunuzu seçin (içerik optimize edilir)</li>
              <li>Görseller varsa AI otomatik analiz eder</li>
              <li>Ne istediğinizi detaylı yazın</li>
              <li>AI size özel içerik oluşturur</li>
              <li>İstediğiniz gibi düzenleyin ve kullanın</li>
            </ul>
            <p className="text-xs text-purple-700 mt-3">
              🔒 OpenAI API kullanılıyor - Verileriniz güvende
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIContentModal;
